import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import type { Message } from '../types';
import { CodeBlock } from './CodeBlock';

interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={clsx('flex gap-3', isUser && 'flex-row-reverse')}>
      <div 
        className={clsx(
          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0",
          isUser ? "bg-secondary-600 text-white" : "bg-primary-500 text-white"
        )}
      >
        {isUser ? 'U' : 'AI'}
      </div>
      
      <div className={clsx('flex flex-col gap-1 flex-1', isUser && 'items-end')}>
        <div className={clsx(
          'message-bubble',
          isUser ? 'user' : 'assistant'
        )}>
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          ) : (
            <ReactMarkdown 
              className="prose prose-invert prose-sm max-w-none"
              remarkPlugins={[remarkGfm]}
              components={{
                code: CodeBlock,
                pre: ({ children }) => <>{children}</>,
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    {children}
                  </a>
                )
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
          
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mt-2 pt-2 border-t border-base-700">
              {message.toolCalls.map((toolCall, index) => (
                <div key={index} className="text-xs text-base-400">
                  {toolCall.name === 'save_fact' && (
                    <span>💾 Saved fact: "{toolCall.arguments.fact_text}"</span>
                  )}
                  {toolCall.name === 'search_conversations' && (
                    <span>🔍 Searched for: "{toolCall.arguments.query}"</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-xs text-base-500">
          <span>{formatDistanceToNow(message.timestamp, { addSuffix: true })}</span>
          {message.status && message.status !== 'sent' && (
            <span 
              className={clsx(
                "px-1.5 py-0.5 rounded",
                message.status === 'sending' && "bg-primary-500/20 text-primary-400",
                message.status === 'error' && "bg-secondary-500/20 text-secondary-400",
                message.status === 'offline' && "bg-accent-500/20 text-accent-400"
              )}
            >
              {message.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}